import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authFetch } from "../../utils/api";
import '../../styles/admin/admin-form.css'; 

const DIAS_SEMANA = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];

const HORARIO_POR_DEFECTO = DIAS_SEMANA.reduce((acc, dia) => {
  acc[dia] = [];
  return acc;
}, {});

const INITIAL_FORM_DATA = {
  nombre: '',
  correoElectronico: '',
  telefono: '',
  municipio: '',
  estado: '',
  direccion: '',
  imagenUrl: '', 
  tipo: '', 
  horario: HORARIO_POR_DEFECTO 
};

export default function FormularioTransportista() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id); 

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [pageLoading, setPageLoading] = useState(isEditMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // --- NUEVO: Estado para configuración masiva ---
  const [generalHorario, setGeneralHorario] = useState([{ abre: "", cierra: "" }]);

  useEffect(() => {
    if (isEditMode) {
      setPageLoading(true);
      setError(null);
      
      authFetch(`/api/Transportistas/${id}`)
        .then(res => {
          if (!res.ok) throw new Error('No se pudieron cargar los datos.');
          return res.json();
        })
        .then(data => {
          setFormData({
            nombre: data.nombre || '',
            correoElectronico: data.correoElectronico || '',
            telefono: data.telefono || '',
            municipio: data.municipio || '',
            estado: data.estado || '',
            direccion: data.direccion || '',
            imagenUrl: data.urlImagen || data.UrlImagen || data.url_imagen || '',
            tipo: data.tipo || '', 
            horario: { ...HORARIO_POR_DEFECTO, ...(data.horario || {}) }
          });
        })
        .catch(err => setError(err.message))
        .finally(() => setPageLoading(false));
    }
  }, [id, isEditMode]); 

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleHorarioChange = (dia, index, campo, value) => {
    setFormData(prev => {
      const nuevoHorario = JSON.parse(JSON.stringify(prev.horario));
      if (nuevoHorario[dia] && nuevoHorario[dia][index]) {
        nuevoHorario[dia][index][campo] = value;
      }
      return { ...prev, horario: nuevoHorario };
    });
  };

  const addTurno = (dia) => {
    setFormData(prev => {
      const nuevoHorario = { ...prev.horario };
      const nuevosSlots = [...(nuevoHorario[dia] || []), { abre: '', cierra: '' }];
      nuevoHorario[dia] = nuevosSlots;
      return { ...prev, horario: nuevoHorario };
    });
  };

  const removeTurno = (dia, index) => {
    setFormData(prev => {
      const nuevoHorario = { ...prev.horario };
      nuevoHorario[dia] = nuevoHorario[dia].filter((_, i) => i !== index);
      return { ...prev, horario: nuevoHorario };
    });
  };

  // --- LÓGICA CONFIGURACIÓN GENERAL ---
  const handleGeneralChange = (index, campo, value) => {
    const nuevos = [...generalHorario];
    nuevos[index] = { ...nuevos[index], [campo]: value };
    setGeneralHorario(nuevos);
  };

  const addGeneralTurn = () => {
    setGeneralHorario([...generalHorario, { abre: "", cierra: "" }]);
  };

  const removeGeneralTurn = (index) => {
    setGeneralHorario(generalHorario.filter((_, i) => i !== index));
  };

  const aplicarHorarioGeneral = () => {
    const turnosValidos = generalHorario.filter(t => t.abre && t.cierra);
    if (turnosValidos.length === 0) {
      alert("⚠️ Define al menos un turno completo en la configuración general.");
      return;
    }
    if (!window.confirm(`¿Aplicar turnos a LUNES a VIERNES?`)) return;

    setFormData(prev => {
      const nuevoHorario = { ...prev.horario };
      ["lunes", "martes", "miercoles", "jueves", "viernes"].forEach(dia => {
        nuevoHorario[dia] = turnosValidos.map(t => ({ ...t }));
      });
      return { ...prev, horario: nuevoHorario };
    });
    alert("✅ Horario aplicado.");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const url = isEditMode ? `/api/Transportistas/${id}` : `/api/Transportistas`;
    const method = isEditMode ? 'PUT' : 'POST';

    const payload = {
      ...(isEditMode && { id: id }), 
      nombre: formData.nombre,
      correoElectronico: formData.correoElectronico,
      telefono: formData.telefono,
      municipio: formData.municipio,
      estado: formData.estado,
      direccion: formData.direccion,
      UrlImagen: formData.imagenUrl, 
      tipo: formData.tipo, 
      horario: formData.horario
    };
    
    try {
      const response = await authFetch(url, { method, body: JSON.stringify(payload) });
      if (!response.ok) throw new Error(await response.text() || 'Error desconocido.');
      alert(isEditMode ? 'Transportista actualizado' : 'Transportista guardado');
      navigate('/admin/transportistas'); 
    } catch (err) {
      setError(err.message);
      alert('Error: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="form-container">
      <button className="btn-back" onClick={() => navigate(-1)} disabled={isSubmitting}>
        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"/></svg> Volver
      </button>

      <h1>{isEditMode ? 'Editar Transportista' : 'Añadir Nuevo Transportista'}</h1>
      {error && <div className="form-error">Error: {error}</div>}
      {pageLoading && <div className="form-loading">Cargando...</div>}

      {!pageLoading && (
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="nombre">Nombre</label>
            <input id="nombre" name="nombre" type="text" value={formData.nombre} onChange={handleChange} disabled={isSubmitting} required />
          </div>

          <div className="form-group">
            <label htmlFor="imagenUrl">URL de Imagen</label>
            <input id="imagenUrl" name="imagenUrl" type="url" value={formData.imagenUrl} onChange={handleChange} disabled={isSubmitting} />
            {formData.imagenUrl && <div style={{ marginTop: '10px' }}><img src={formData.imagenUrl} alt="Preview" style={{ maxWidth: '150px', objectFit: 'contain' }} onError={(e) => e.target.style.display = 'none'} /></div>}
          </div>

          <div className="form-group">
            <label htmlFor="tipo">Tipo</label>
            <select id="tipo" name="tipo" value={formData.tipo} onChange={handleChange} disabled={isSubmitting} required>
              <option value="">-- Seleccione --</option>
              <option value="terrestre">Terrestre</option>
              <option value="ferrocarril">Ferrocarril</option>
            </select>
          </div>

          <div className="form-group"><label>Correo</label><input name="correoElectronico" type="email" value={formData.correoElectronico} onChange={handleChange} disabled={isSubmitting} /></div>
          <div className="form-group"><label>Teléfono</label><input name="telefono" type="tel" value={formData.telefono} onChange={handleChange} disabled={isSubmitting} /></div>
          <div className="form-row">
            <div className="form-group"><label>Municipio</label><input name="municipio" type="text" value={formData.municipio} onChange={handleChange} disabled={isSubmitting} /></div>
            <div className="form-group"><label>Estado</label><input name="estado" type="text" value={formData.estado} onChange={handleChange} disabled={isSubmitting} /></div>
          </div>
          <div className="form-group"><label>Dirección</label><textarea name="direccion" rows="3" value={formData.direccion} onChange={handleChange} disabled={isSubmitting}></textarea></div>

          <fieldset>
            <legend>Horarios</legend>

            {/* PANEL DE CONFIGURACIÓN GENERAL */}
            <div style={{ backgroundColor: '#e0f2fe', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '2px solid #0ea5e9' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h4 style={{ margin: 0, color: '#0369a1' }}>⚡ Configurar Horario General (Lunes a Viernes)</h4>
                <button type="button" onClick={addGeneralTurn} style={{ fontSize: '0.8em', padding: '4px 8px', cursor: 'pointer', backgroundColor: '#fff', border: '1px solid #0369a1', color: '#0369a1', borderRadius: '4px' }}>+ Turno</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {generalHorario.map((turno, index) => (
                  <div key={index} style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}><label style={{ fontSize: '0.8em' }}>Apertura</label><input type="time" value={turno.abre} onChange={(e) => handleGeneralChange(index, 'abre', e.target.value)} style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }} /></div>
                    <div className="form-group" style={{ marginBottom: 0 }}><label style={{ fontSize: '0.8em' }}>Cierre</label><input type="time" value={turno.cierra} onChange={(e) => handleGeneralChange(index, 'cierra', e.target.value)} style={{ padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }} /></div>
                    {generalHorario.length > 1 && <button type="button" onClick={() => removeGeneralTurn(index)} style={{ marginBottom: '2px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', width: '30px', height: '30px', cursor: 'pointer' }}>&times;</button>}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '15px' }}><button type="button" onClick={aplicarHorarioGeneral} style={{ backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Aplicar Horario</button></div>
            </div>

            {DIAS_SEMANA.map((dia) => (
              <div key={dia} className="horario-dia">
                <div className="horario-dia-header">
                  <h3>{dia.charAt(0).toUpperCase() + dia.slice(1)}</h3>
                  <button type="button" onClick={() => addTurno(dia)} className="btn-add-turno" disabled={isSubmitting}>+ Añadir turno</button>
                </div>
                {formData.horario[dia].length === 0 && <p className="horario-cerrado">Cerrado</p>}
                {formData.horario[dia].map((turno, index) => (
                  <div key={index} className="horario-turno">
                    <div className="horario-inputs">
                      <div className="form-group"><label>Abre</label><input type="time" value={turno.abre} onChange={(e) => handleHorarioChange(dia, index, 'abre', e.target.value)} disabled={isSubmitting} /></div>
                      <div className="form-group"><label>Cierra</label><input type="time" value={turno.cierra} onChange={(e) => handleHorarioChange(dia, index, 'cierra', e.target.value)} disabled={isSubmitting} /></div>
                    </div>
                    <button type="button" onClick={() => removeTurno(dia, index)} className="btn-remove-turno" disabled={isSubmitting}>&times;</button>
                  </div>
                ))}
              </div>
            ))}
          </fieldset>
          
          <button type="submit" className="btn-submit" disabled={isSubmitting || pageLoading}>{isSubmitting ? 'Guardando...' : (isEditMode ? 'Guardar Cambios' : 'Guardar')}</button>
        </form>
      )} 
    </div>
  );
}